import { humanoidFace, humanoidSkull } from './humanoid-head.js';
import type { HumanFace, HumanRigId, HumanSkull } from './human.js';
import { NOSE_SHAPE_DEFAULT, type NoseShape } from './humanoid-nose.js';

/** Dwarf facial proportions, in the shared skull coordinates. */
export interface DwarfFaceMorph extends NoseShape {
  jawWidth: number; cheekWidth: number; brow: number; cheek: number; hollow: number;
  noseWidth: number; noseLength: number; noseProjection: number; noseRootHeight: number; noseBridge: number;
  mouthWidth: number; lipFullness: number; eyeSize: number; eyeDepth: number;
  /** Forward offset of the eye-level cross-section from the matching Human skull. */
  eyeLevelCentreOffset: number;
  /** Orbital plane before the Eye depth inset; the morph control shows the resulting skull plane. */
  eyePlane: number; eyeOpening: number;
  earLength: number; earWidth: number;
}
export const DWARF_FACE_DEFAULT: DwarfFaceMorph = {
  ...NOSE_SHAPE_DEFAULT, bridgeWidth: 1.12, tipWidth: 1.18, wingWidth: 1.1,
  jawWidth: 1, cheekWidth: 1, brow: .008, cheek: .013, hollow: .003,
  noseWidth: 1.25, noseLength: .036, noseProjection: .105, noseRootHeight: 1.861, noseBridge: .003,
  mouthWidth: .027, lipFullness: .95, eyeSize: .97, eyeLevelCentreOffset: 0, eyePlane: .0855, eyeDepth: .008, eyeOpening: .78,
  earLength: .027, earWidth: .019,
};
export const dwarfFaces: { id: string; name: string; morph?: Partial<DwarfFaceMorph> }[] = [
  { id: 'standard', name: 'Dwarf classic' },
  { id: 'broad', name: 'Stone jaw', morph: { jawWidth: 1.12, cheekWidth: 1.05, brow: .011, mouthWidth: .03 } },
  { id: 'angular', name: 'Carved features', morph: { jawWidth: .96, cheek: .022, hollow: .008, eyeDepth: .01 } },
  { id: 'soft', name: 'Rounded features', morph: { brow: .005, hollow: .001, lipFullness: 1.15, eyeOpening: .87 } },
  { id: 'aquiline', name: 'Strong nose', morph: { noseProjection: .112, noseLength: .043, noseBridge: .006 } },
  { id: 'gaunt', name: 'Weathered', morph: { jawWidth: .97, hollow: .01, eyeDepth: .011, brow: .009 } },
  { id: 'youthful', name: 'Youthful', morph: { brow: .004, noseLength: .032, eyeSize: 1.07, eyeOpening: .9 } },
];
export function resolveDwarfFace(base: DwarfFaceMorph, id = 'standard'): DwarfFaceMorph {
  return { ...base, ...dwarfFaces.find(face => face.id === id)?.morph };
}
export function dwarfSkull(m: DwarfFaceMorph, body: HumanRigId): HumanSkull {
  const skull = humanoidSkull(body, 1.25 * m.jawWidth, 1.1 * m.cheekWidth);
  skull.jaw.centre[5] += m.eyeLevelCentreOffset;
  return {
    ...skull,
    eye: { x: .034, y: 1.857, plane: m.eyePlane - m.eyeDepth * .5, opening: m.eyeOpening },
    ear: { ...skull.ear, length: m.earLength, width: m.earWidth, thickness: .0075 },
    mouth: { y: 1.795, width: m.mouthWidth, upper: 1, lower: 1, forward: 1, bow: .9, corners: .0006, groove: .0006, overlap: .0008, lift: .0004 },
    nose: { rootY: m.noseRootHeight, length: m.noseLength, tipZ: m.noseProjection,
      ...Object.fromEntries(Object.keys(NOSE_SHAPE_DEFAULT).map(key => [key, m[key as keyof NoseShape]])) },
  };
}
export function dwarfFaceShape(m: DwarfFaceMorph, body: HumanRigId = 'human-male'): HumanFace {
  return { ...humanoidFace(body, { jaw: 1.08, chin: 1.06, square: 1.15 }, { chinDepth: .002 }), brow: m.brow, cheek: m.cheek, hollow: m.hollow, eye: m.eyeDepth, eyeSize: m.eyeSize,
    nose: m.noseWidth, lips: m.lipFullness, noseLength: 0, bridge: m.noseBridge, fullness: .002,
    eyeTilt: -.01, fold: 0 };
}
